import React from "react";
import { View, Text, StyleSheet, Pressable, Platform, Linking } from "react-native";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import { Reveal, LiveDot, WebClass, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "./_shared";

// =============================================================================
// NtVoxcraftSiplense — promotes SipLens as the desktop/mobile softphone that
// connects to a Voxcraft PBX. Mirrors the shape of the SipLens marketing hero
// (eyebrow → split headline → lede → CTA row → platform strip → device-frame
// mockup) but re-skinned to Voxcraft fonts and theme tokens.
// =============================================================================

const SIPLENS_URL = "https://siplense.app";

const PLATFORMS = [
  { label: "macOS" },
  { label: "Windows" },
  { label: "iOS" },
  { label: "Android" },
  { label: "Linux", suffix: "beta" },
];

const PAD_KEYS = [
  { d: "1", l: "" },
  { d: "2", l: "ABC" },
  { d: "3", l: "DEF" },
  { d: "4", l: "GHI" },
  { d: "5", l: "JKL" },
  { d: "6", l: "MNO" },
  { d: "7", l: "PQRS" },
  { d: "8", l: "TUV" },
  { d: "9", l: "WXYZ" },
  { d: "*", l: "" },
  { d: "0", l: "+" },
  { d: "#", l: "" },
];

const PACKETS = [
  { t: "00:00.012", dir: "→", msg: "REGISTER",   tag: "TX" },
  { t: "00:00.034", dir: "←", msg: "200 OK",     tag: "OK" },
  { t: "00:00.211", dir: "→", msg: "INVITE",     tag: "TX" },
  { t: "00:00.218", dir: "←", msg: "100 Trying", tag: "RX" },
  { t: "00:00.412", dir: "←", msg: "180 Ringing", tag: "RX" },
  { t: "00:02.087", dir: "←", msg: "200 OK",     tag: "OK" },
];

const STATS = [
  { k: "CODEC",  v: "OPUS",   sub: "48 kHz · stereo" },
  { k: "JITTER", v: "8 ms",   sub: "avg over 30s" },
  { k: "LOSS",   v: "0.2%",   sub: "3 of 1,482" },
  { k: "MOS",    v: "4.3",    sub: "excellent" },
];

export default function NtVoxcraftSiplense() {
  const { theme } = useTheme();
  const isMobile = useMobile();

  const openSite = () => {
    if (Platform.OS === "web") window.open(SIPLENS_URL, "_blank", "noopener,noreferrer");
    else Linking.openURL(SIPLENS_URL);
  };

  return (
    <View nativeID="softphone" style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={styles.container}>
        {/* Heading block */}
        <Reveal>
          <View style={styles.head}>
            <View style={[styles.eyebrow, { borderColor: theme.pillBorder, backgroundColor: theme.pillBg }]}>
              <LiveDot style={{ marginRight: 8 }} />
              <Text style={[styles.eyebrowText, { color: theme.brand2 }]}>
                DESKTOP &amp; MOBILE CLIENT · V1.0
              </Text>
            </View>

            <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>
              The softphone for your lines,{" "}
              <Text style={[styles.titleSoft, { color: theme.textDim }]}>native on every platform.</Text>
            </Text>

            <Text style={[styles.lede, { color: theme.textDim }]}>
              SipLens is the companion softphone for Voxcraft. Multi-account SIP, contacts, and voicemail wrapped
              around a built-in packet inspector and an AI agent that reads your traces and tells you what broke.
              Connects to your Voxcraft PBX in seconds.
            </Text>

            <View style={[styles.ctaRow, isMobile && styles.ctaRowMobile]}>
              <WebClass className="voxcraft-btn voxcraft-btn-primary">
                <Pressable
                  onPress={openSite}
                  style={({ pressed }) => [
                    styles.btn,
                    styles.btnPrimary,
                    { backgroundColor: theme.brand2 },
                    pressed && styles.btnPressed,
                  ]}
                >
                  <Text style={[styles.btnText, { color: "#fff" }]}>Download for macOS</Text>
                </Pressable>
              </WebClass>
              <WebClass className="voxcraft-btn">
                <Pressable
                  onPress={openSite}
                  style={({ pressed }) => [
                    styles.btn,
                    styles.btnGhost,
                    { borderColor: theme.borderSoft, backgroundColor: theme.surface },
                    pressed && styles.btnPressed,
                  ]}
                >
                  <Text style={[styles.btnText, { color: theme.text }]}>See features →</Text>
                </Pressable>
              </WebClass>
            </View>

            <View style={styles.platformStrip}>
              {PLATFORMS.map((p, i) => (
                <React.Fragment key={p.label}>
                  {i > 0 && <View style={[styles.platformDivider, { backgroundColor: theme.borderSoft }]} />}
                  <View style={styles.platformItem}>
                    <Text style={[styles.platformText, { color: theme.textMute }]}>{p.label}</Text>
                    {p.suffix && (
                      <Text style={[styles.platformSuffix, { color: theme.textMute }]}> ({p.suffix})</Text>
                    )}
                  </View>
                </React.Fragment>
              ))}
            </View>
          </View>
        </Reveal>

        {/* Device-frame mockup */}
        <Reveal delay={2}>
          <View
            style={[
              styles.frame,
              {
                borderColor: theme.borderSoft,
                backgroundColor: theme.bg2,
                shadowColor: "#000",
              },
            ]}
          >
            {/* Title bar with traffic lights */}
            <View style={[styles.frameBar, { borderBottomColor: theme.borderSoft }]}>
              <View style={styles.frameDots}>
                <View style={[styles.frameDot, { backgroundColor: "#ff5f57" }]} />
                <View style={[styles.frameDot, { backgroundColor: "#febc2e" }]} />
                <View style={[styles.frameDot, { backgroundColor: "#28c840" }]} />
              </View>
              <Text style={[styles.frameTitle, { color: theme.textMute }]}>SipLens — Dialer</Text>
              <View style={{ width: 60 }} />
            </View>

            <View style={[styles.frameBody, isMobile && styles.frameBodyMobile]}>
              {/* Sidebar — hidden on mobile */}
              {!isMobile && (
                <View style={[styles.sidebar, { borderRightColor: theme.borderSoft }]}>
                  <SidebarGroup theme={theme} title="PHONE" items={["Dialer", "History", "Contacts", "Voicemail"]} active={0} />
                  <SidebarGroup theme={theme} title="TOOLS" items={["Packet Inspector", "AI Agent", "Diagnostics"]} active={-1} />
                  <View style={[styles.sidebarAccountsHeader, { borderTopColor: theme.borderSoft }]}>
                    <Text style={[styles.sidebarGroupTitle, { color: theme.textMute }]}>ACCOUNTS</Text>
                  </View>
                  <SidebarAccount theme={theme} name="Voxcraft · Office" color="#4cb782" />
                  <SidebarAccount theme={theme} name="Personal" color={theme.brand2} />
                  <SidebarAccount theme={theme} name="Lab" color="#f1b250" />
                </View>
              )}

              {/* Center — dialer */}
              <View style={styles.center}>
                <View style={styles.centerHead}>
                  <Text style={[styles.centerTitle, { color: theme.text }]}>Dialer</Text>
                  <Text style={[styles.centerVia, { color: theme.textMute }]}>via Voxcraft · Office ▾</Text>
                </View>

                <View style={[styles.numberBox, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}>
                  <Text style={[styles.numberText, { color: theme.text }]}>+1 305 555 0142</Text>
                  <View style={[styles.numberCursor, { backgroundColor: theme.brand2 }]} />
                </View>

                <View style={styles.padGrid}>
                  {PAD_KEYS.map((k) => (
                    <View key={k.d} style={styles.padCell}>
                      <View style={[styles.padKey, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}>
                        <Text style={[styles.padDigit, { color: theme.text }]}>{k.d}</Text>
                        {!!k.l && <Text style={[styles.padLetters, { color: theme.textMute }]}>{k.l}</Text>}
                      </View>
                    </View>
                  ))}
                </View>

                <View style={styles.actionRow}>
                  <Pressable
                    onPress={openSite}
                    style={({ pressed }) => [
                      styles.callBtn,
                      { backgroundColor: "#4cb782" },
                      pressed && styles.btnPressed,
                    ]}
                  >
                    <Text style={styles.callBtnText}>● Call</Text>
                  </Pressable>
                  <View style={[styles.utilBtn, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}>
                    <Text style={[styles.utilBtnText, { color: theme.textDim }]}>Mute</Text>
                  </View>
                  <View style={[styles.utilBtn, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}>
                    <Text style={[styles.utilBtnText, { color: theme.textDim }]}>Hold</Text>
                  </View>
                </View>
              </View>

              {/* Inspector — hidden on mobile and narrow */}
              {!isMobile && (
                <View style={[styles.inspector, { borderLeftColor: theme.borderSoft }]}>
                  <View style={styles.inspectorHead}>
                    <LiveDot style={{ marginRight: 6 }} />
                    <Text style={[styles.inspectorHeadText, { color: "#4cb782" }]}>LIVE</Text>
                    <Text style={[styles.inspectorSub, { color: theme.textMute }]}>PACKET INSPECTOR</Text>
                  </View>

                  <View style={styles.packetList}>
                    {PACKETS.map((p, i) => (
                      <View key={i} style={styles.packetRow}>
                        <Text style={[styles.packetTime, { color: theme.textMute }]}>{p.t}</Text>
                        <Text style={[styles.packetDir, { color: p.dir === "→" ? theme.brand2 : theme.green }]}>
                          {p.dir}
                        </Text>
                        <Text style={[styles.packetMsg, { color: theme.text }]}>{p.msg}</Text>
                        <Text style={[styles.packetTag, { color: theme.textMute }]}>{p.tag}</Text>
                      </View>
                    ))}
                  </View>

                  <View style={[styles.statsGrid, { borderTopColor: theme.borderSoft }]}>
                    {STATS.map((s) => (
                      <View key={s.k} style={styles.statCell}>
                        <Text style={[styles.statK, { color: theme.textMute }]}>{s.k}</Text>
                        <Text style={[styles.statV, { color: theme.text }]}>{s.v}</Text>
                        <Text style={[styles.statSub, { color: theme.textMute }]}>{s.sub}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </View>
          </View>
        </Reveal>
      </View>
    </View>
  );
}

function SidebarGroup({ theme, title, items, active }) {
  return (
    <View style={styles.sidebarGroup}>
      <Text style={[styles.sidebarGroupTitle, { color: theme.textMute }]}>{title}</Text>
      {items.map((it, i) => {
        const isActive = i === active;
        return (
          <View
            key={it}
            style={[
              styles.sidebarItem,
              isActive && { backgroundColor: "rgba(37,112,189,0.16)" },
            ]}
          >
            <Text
              style={[
                styles.sidebarItemText,
                { color: isActive ? theme.text : theme.textDim },
                isActive && { fontWeight: "600" },
              ]}
            >
              {it}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function SidebarAccount({ theme, name, color }) {
  return (
    <View style={styles.sidebarAccount}>
      <View style={[styles.sidebarAccountDot, { backgroundColor: color }]} />
      <Text style={[styles.sidebarAccountText, { color: theme.textDim }]}>{name}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28 },
  container: { maxWidth: 1180, marginHorizontal: "auto", width: "100%" },

  // -------- Head --------
  head: { maxWidth: 760, marginBottom: 48 },
  eyebrow: {
    flexDirection: "row", alignItems: "center", alignSelf: "flex-start",
    paddingHorizontal: 11, paddingVertical: 5, borderRadius: 999, borderWidth: 1, marginBottom: 18,
  },
  eyebrowText: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6 },

  title: {
    fontFamily: FONT_DISPLAY, fontSize: 46, fontWeight: "800",
    letterSpacing: -1.2, lineHeight: 52, marginBottom: 18,
  },
  titleMobile: { fontSize: 34, lineHeight: 40 },
  titleSoft: { fontWeight: "500" },

  lede: { fontFamily: FONT_BODY, fontSize: 17, lineHeight: 27, marginBottom: 28, maxWidth: 640 },

  ctaRow: { flexDirection: "row", gap: 12, flexWrap: "wrap", marginBottom: 22 },
  ctaRowMobile: { width: "100%" },
  btn: { paddingHorizontal: 22, paddingVertical: 13, borderRadius: 8, borderWidth: 1, borderColor: "transparent", alignItems: "center", justifyContent: "center" },
  btnPrimary: {
    shadowColor: "#2570BD", shadowOpacity: 0.35, shadowRadius: 16, shadowOffset: { width: 0, height: 6 },
  },
  btnGhost: {},
  btnText: { fontFamily: FONT_BODY, fontSize: 14, fontWeight: "600" },
  btnPressed: { opacity: 0.85, transform: [{ translateY: -1 }] },

  platformStrip: { flexDirection: "row", alignItems: "center", flexWrap: "wrap" },
  platformDivider: { width: 1, height: 12, marginHorizontal: 14 },
  platformItem: { flexDirection: "row", alignItems: "baseline", paddingVertical: 2 },
  platformText: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "600", letterSpacing: 1.2, textTransform: "uppercase" },
  platformSuffix: { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 0.8, textTransform: "lowercase" },

  // -------- Mockup frame --------
  frame: {
    borderWidth: 1, borderRadius: 16, overflow: "hidden",
    shadowOpacity: 0.45, shadowRadius: 40, shadowOffset: { width: 0, height: 18 },
  },
  frameBar: {
    flexDirection: "row", alignItems: "center", justifyContent: "space-between",
    paddingHorizontal: 14, paddingVertical: 10, borderBottomWidth: 1,
  },
  frameDots: { flexDirection: "row", gap: 7, width: 60 },
  frameDot: { width: 12, height: 12, borderRadius: 6 },
  frameTitle: { fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.6 },

  frameBody: { flexDirection: "row", minHeight: 480 },
  frameBodyMobile: { minHeight: 460 },

  // sidebar
  sidebar: {
    width: 220, borderRightWidth: 1, paddingVertical: 18, paddingHorizontal: 14,
    backgroundColor: "#08080b",
  },
  sidebarGroup: { marginBottom: 18 },
  sidebarGroupTitle: { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1.2, marginBottom: 10, textTransform: "uppercase" },
  sidebarItem: { paddingHorizontal: 10, paddingVertical: 7, borderRadius: 6, marginBottom: 2 },
  sidebarItemText: { fontFamily: FONT_BODY, fontSize: 13 },
  sidebarAccountsHeader: { borderTopWidth: 1, paddingTop: 14, marginTop: 4 },
  sidebarAccount: { flexDirection: "row", alignItems: "center", paddingHorizontal: 10, paddingVertical: 6 },
  sidebarAccountDot: { width: 7, height: 7, borderRadius: 4, marginRight: 9 },
  sidebarAccountText: { fontFamily: FONT_BODY, fontSize: 13 },

  // center
  center: { flex: 1, padding: 28 },
  centerHead: { flexDirection: "row", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 },
  centerTitle: { fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: "700", letterSpacing: -0.4 },
  centerVia: { fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase" },

  numberBox: {
    flexDirection: "row", alignItems: "center", justifyContent: "center",
    paddingVertical: 18, borderRadius: 10, borderWidth: 1, marginBottom: 20,
  },
  numberText: { fontFamily: FONT_MONO, fontSize: 22, fontWeight: "600", letterSpacing: 1 },
  numberCursor: { width: 2, height: 22, marginLeft: 4, opacity: 0.9 },

  padGrid: { flexDirection: "row", flexWrap: "wrap", marginHorizontal: -5, marginBottom: 16 },
  padCell: { width: "33.33%", padding: 5 },
  padKey: { paddingVertical: 12, borderRadius: 10, borderWidth: 1, alignItems: "center" },
  padDigit: { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: "600", letterSpacing: -0.4, lineHeight: 26 },
  padLetters: { fontFamily: FONT_MONO, fontSize: 9, letterSpacing: 1.2, marginTop: 2 },

  actionRow: { flexDirection: "row", gap: 10, marginTop: 4 },
  callBtn: {
    flex: 1, paddingVertical: 14, borderRadius: 10, alignItems: "center", justifyContent: "center",
    shadowColor: "#4cb782", shadowOpacity: 0.35, shadowRadius: 14, shadowOffset: { width: 0, height: 6 },
  },
  callBtnText: { color: "#fff", fontFamily: FONT_BODY, fontSize: 14, fontWeight: "700", letterSpacing: 0.4 },
  utilBtn: {
    paddingHorizontal: 18, paddingVertical: 14, borderRadius: 10, borderWidth: 1,
    alignItems: "center", justifyContent: "center",
  },
  utilBtnText: { fontFamily: FONT_BODY, fontSize: 13, fontWeight: "600" },

  // inspector
  inspector: {
    width: 320, borderLeftWidth: 1, padding: 18,
    backgroundColor: "#08080b",
  },
  inspectorHead: { flexDirection: "row", alignItems: "center", marginBottom: 14 },
  inspectorHeadText: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.4 },
  inspectorSub: { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1.2, marginLeft: "auto" },

  packetList: { marginBottom: 14 },
  packetRow: {
    flexDirection: "row", alignItems: "center",
    paddingVertical: 6, paddingHorizontal: 4,
  },
  packetTime: { fontFamily: FONT_MONO, fontSize: 10, width: 68 },
  packetDir: { fontFamily: FONT_MONO, fontSize: 12, fontWeight: "700", width: 18 },
  packetMsg: { fontFamily: FONT_MONO, fontSize: 11.5, flex: 1 },
  packetTag: { fontFamily: FONT_MONO, fontSize: 9, letterSpacing: 0.8 },

  statsGrid: { flexDirection: "row", flexWrap: "wrap", borderTopWidth: 1, paddingTop: 14, marginHorizontal: -6 },
  statCell: { width: "50%", padding: 6 },
  statK: { fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: 1.2, marginBottom: 4, textTransform: "uppercase" },
  statV: { fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: "700", letterSpacing: -0.4, lineHeight: 22 },
  statSub: { fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: 0.6, marginTop: 2 },
});
